import axios from 'axios';
import { API_URL } from '../../config';
import { getAuthToken } from '../../utils/authToken';
import { serializeDashboardParams } from '../../utils/dashboardStatusFilter';
import type { ExportIdListResponse, TicketExportIdParams } from '../exportIdQuery';

export type ExportArea = 'tickets' | 'purchases';
export type SelectionMode = 'automatic' | 'manual';

export interface ExportFieldItem {
  field_id: number;
  header: string;
  label: string;
  name: string;
  field_type: string;
  field_type_label: string;
  workflow_id: number;
  workflow_name: string;
  group_name?: string | null;
  branch_context: string;
  order: number;
}

export interface ExportPreviewRow {
  record_id: number;
  public_id: string;
  title: string;
  status: string;
  workflow_name: string;
  cells: Record<string, string>;
}

export interface ExportPreviewResponse {
  columns: ExportFieldItem[];
  rows: ExportPreviewRow[];
  total: number;
  subtask_count: number;
  truncated: boolean;
  diagnostics: Array<{ code?: string; message?: string }>;
  generated_at: string;
  timezone: string;
}

export interface RecordExportFilters {
  record_id?: string;
  status?: string;
  statuses?: string[];
  urgency?: string;
  priority?: string;
  assigned_group_id?: number;
  workflow_id?: number;
  search?: string;
  created_from?: string;
  created_to?: string;
  include_subtasks?: boolean;
  closed_visibility?: string;
}

export function createExportCancelToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `export-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function authHeaders() {
  return { Authorization: `Bearer ${getAuthToken()}` };
}

export function isExportAbortedError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  return error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.response?.status === 499;
}

export async function requestExportCancellation(cancelToken: string): Promise<void> {
  try {
    await axios.post(`${API_URL}/api/exports/cancel`, { cancel_token: cancelToken }, { headers: authHeaders() });
  } catch {
    // best-effort
  }
}

type QueryParams = Record<string, string | number | boolean | string[] | undefined>;

export async function fetchExportIds(
  area: ExportArea,
  params: QueryParams | TicketExportIdParams,
): Promise<ExportIdListResponse> {
  const response = await axios.get<ExportIdListResponse>(`${API_URL}/api/exports/${area}/ids`, {
    params,
    paramsSerializer: { serialize: serializeDashboardParams },
  });
  return response.data;
}

export async function fetchExportFields(
  area: ExportArea,
  params: QueryParams | TicketExportIdParams,
): Promise<ExportFieldItem[]> {
  const response = await axios.get<{ fields: ExportFieldItem[] }>(`${API_URL}/api/exports/${area}/fields`, {
    params,
    paramsSerializer: { serialize: serializeDashboardParams },
  });
  return response.data.fields || [];
}

export async function fetchExportPreview(
  area: ExportArea,
  body: {
    filters: RecordExportFilters;
    custom_field_ids?: number[];
    selection_mode: SelectionMode;
    sample_size?: number;
    cancel_token?: string;
  },
  signal?: AbortSignal,
): Promise<ExportPreviewResponse> {
  const response = await axios.post<ExportPreviewResponse>(`${API_URL}/api/exports/${area}/preview`, body, {
    headers: authHeaders(),
    signal,
  });
  return response.data;
}

function extractFilename(disposition: string): string | null {
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      return encoded[1];
    }
  }
  const quoted = disposition.match(/filename="([^"]+)"/i);
  if (quoted) return quoted[1];
  const plain = disposition.match(/filename=([^;]+)/i);
  return plain ? plain[1].trim() : null;
}

export async function downloadExportFile(
  area: ExportArea,
  body: {
    filters: RecordExportFilters;
    custom_field_ids?: number[];
    selection_mode: SelectionMode;
    cancel_token?: string;
  },
  signal?: AbortSignal,
): Promise<void> {
  const response = await axios.post(`${API_URL}/api/exports/${area}/file`, body, {
    headers: authHeaders(),
    responseType: 'blob',
    signal,
    transformResponse: [(data) => data],
  });
  const disposition = String(response.headers['content-disposition'] || '');
  const filename = extractFilename(disposition) || `Suportnect_${area}.xlsx`;
  const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(url), 200);
}
